<template>
    <div class="row">&nbsp;</div>
    <div class="xForm">
        <form @submit.prevent="handleSubmit">
            <div class="form-group">
                <label for="BusName">Business Name / Business Message</label>
                <input id="BusName" class="form-control" type="text" required v-model="BusName" />
                <div v-if="BusNameNote" class="noteError">{{ BusNmeNote }}</div>
            </div>
            <div class="form-group">
                <label for="codeDescription">Code Description</label>
                <textarea id="codeDescription" class="form-control" rows="3" v-model="codeDescription"></textarea>
            </div>
            <div class="thisOr">
                <div class="formSectionLabel">Required</div>
                <div class="form-group">
                    <label for="redirecturl" class="label>">Redirect URL</label>
                    <input id="redirecturl" class="form-control" type="url" v-model="redirecturl"
                        placeholder="https://example.com"
                        pattern="[Hh][Tt][Tt][Pp][Ss]?:\/\/(?:(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)(?:\.(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)*(?:\.(?:[a-zA-Z\u00a1-\uffff]{2,}))(?::\d{2,5})?(?:\/[^\s]*)?" />
                    <RedirectSafetyStatus :state="redirectSafetyState" :tooltip="redirectSafetyTooltip" />
                </div>
            </div>
            <div v-if="messageNote" class="noteError">{{ messageNote }}</div>
            <div class="form-group fatfinger">
                <input id="upcSupport" class="form-check-input" type="checkbox" v-model="upcSupport" default="false">
                &nbsp;&nbsp;<label for="upcSupport" class="label">UPC (GTIN) Support?</label>
            </div>
            <div v-if="upcSupport" class="thisOr">
                <div class="formSectionLabel">UPC Code Support (optional)</div>
                <div class="form-group">
                    <label for="upc" class="label">UPC Code</label>
                    <input id="upc" class="form-control" @keyup="checkUPC" @blur="syncGs1Ai01FromGtin" type="text" v-model="upc"
                        :placeholder="upcSuggestion" />
                    <div class="upcError">{{ upcError }}</div>
                    <div class="notes">
                        <small>Once 12 digits are entered the UPC barcode will be generated automatically when the code
                            is
                            saved. Correct check digit is required.</small>
                    </div>
                </div>
                <div class="form-group">
                    <input id="calcCheckDigit" class="form-check-input" type="checkbox" v-model="calcCheckDigit"
                        default="false">
                    &nbsp;&nbsp;<label for="calcCheckDigit" class="label">Calculate Check Digit?</label>
                    <div class="notes"><small>Type or paste in 11 digits and the form will calculate and insert the
                            final
                            check digit.</small>
                    </div>
                </div>
                <div class="form-group fatfinger">
                    <input id="gs1Support" class="form-check-input" type="checkbox" v-model="gs1Support" default="false">
                    &nbsp;&nbsp;<label for="gs1Support" class="label">GS1 2D Payloads?</label>
                </div>
                <div class="thisOr" v-if="gs1Support">
                    <div class="formSectionLabel">GS1 2D Payloads (optional)</div>
                    <div class="form-group">
                        <div class="labelWithHelp">
                            <label for="gs1DigitalLinkBaseUri" class="label">GS1 Digital Link Base URI</label>
                            <button
                                type="button"
                                class="helpLink"
                                :title="gs1DigitalLinkBaseUriHelpText"
                                :aria-label="gs1DigitalLinkBaseUriHelpText"
                            >?</button>
                        </div>
                        <input id="gs1DigitalLinkBaseUri" class="form-control" type="url" v-model="gs1DigitalLinkBaseUri" />
                    </div>
                    <div class="form-group">
                        <div class="labelWithHelp">
                            <label class="label">GS1 Application Identifiers</label>
                            <a class="helpLink" href="https://ref.gs1.org/ai/?lang=en" target="_blank"
                                rel="noopener noreferrer" aria-label="Open GS1 AI reference">?</a>
                        </div>
                        <div v-for="(entry, index) in gs1AiEntries" :key="`gs1-ai-${index}`" class="gs1AiRow">
                            <input class="form-control gs1AiCode" type="text" inputmode="numeric" maxlength="4"
                                v-model="entry.ai" placeholder="AI" />
                            <input class="form-control gs1AiValue" type="text" v-model="entry.value"
                                :placeholder="entry.ai === '01' ? '09521234543213' : entry.ai === '10' ? 'ABC123' : entry.ai === '17' ? '260930' : 'Value'" />
                            <button v-if="!entry.isDefault" type="button" class="btn btn-outline-secondary btn-sm"
                                @click="removeGs1AiEntry(index)">Remove</button>
                        </div>
                        <button type="button" class="btn btn-secondary btn-sm" @click="addGs1AiEntry">Add AI</button>
                        <div class="notes"><small>`01`, `10`, and `17` are included by default. Additional AI/value pairs can be added below.</small></div>
                    </div>
                    <div class="form-group">
                        <label class="label">Derived GS1 Element String</label>
                        <textarea class="form-control jsonOptionsInput" rows="3" :value="derivedGs1ElementString" readonly></textarea>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <button type="button" class="btn btn-secondary btn-sm" @click="toggleAdvancedBarcodeOptions">
                    {{ showAdvancedBarcodeOptions ? 'Hide' : 'Show' }} Advanced Barcode Rendering Options
                </button>
            </div>
            <div class="thisOr" v-if="showAdvancedBarcodeOptions">
                <div class="formSectionLabel">Common Barcode Rendering Options</div>
                <div class="form-group">
                    <label class="label">QR Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="qrWidth" class="label">Width</label>
                            <input id="qrWidth" class="form-control" type="number" min="1" v-model.number="qrWidth" />
                        </div>
                        <div>
                            <label for="qrColor" class="label">Color</label>
                            <input id="qrColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(qrColor)"
                                @input="updateColorField('qrColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="qrBackground" class="label">Background</label>
                            <input id="qrBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(qrBackground)"
                                @input="updateColorField('qrBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="qrErrorCorrectionLevel" class="label">Error Correction</label>
                            <select id="qrErrorCorrectionLevel" class="form-control" v-model="qrErrorCorrectionLevel">
                                <option value="L">L</option>
                                <option value="M">M</option>
                                <option value="Q">Q</option>
                                <option value="H">H</option>
                            </select>
                        </div>
                        <div>
                            <label for="qrBorder" class="label">Border</label>
                            <input id="qrBorder" class="form-control" type="number" min="0" v-model.number="qrBorder" />
                        </div>
                        <div>
                            <label for="qrBaseurl" class="label">Base URL</label>
                            <input id="qrBaseurl" class="form-control" type="text" v-model="qrBaseurl" />
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label class="label">GTIN Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="gtinFormat" class="label">Format</label>
                            <select id="gtinFormat" class="form-control" v-model="gtinFormat">
                                <option v-for="format in gtinFormats" :key="format" :value="format">{{ format }}</option>
                            </select>
                        </div>
                        <div>
                            <label for="gtinWidth" class="label">Width</label>
                            <input id="gtinWidth" class="form-control" type="number" min="1" v-model.number="gtinWidth" />
                        </div>
                        <div>
                            <label for="gtinHeight" class="label">Height</label>
                            <input id="gtinHeight" class="form-control" type="number" min="1" v-model.number="gtinHeight" />
                        </div>
                        <div>
                            <label for="gtinScale" class="label">Scale</label>
                            <input id="gtinScale" class="form-control" type="number" min="1" v-model.number="gtinScale" />
                        </div>
                        <div>
                            <label for="gtinIncludeText" class="label">Include Text</label>
                            <input id="gtinIncludeText" class="form-check-input" type="checkbox" v-model="gtinIncludeText" />
                        </div>
                        <div>
                            <label for="gtinTextsize" class="label">Text Size</label>
                            <input id="gtinTextsize" class="form-control" type="number" min="1" v-model.number="gtinTextsize" />
                        </div>
                        <div>
                            <label for="gtinColor" class="label">Color</label>
                            <input id="gtinColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gtinColor)"
                                @input="updateColorField('gtinColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gtinBackground" class="label">Background</label>
                            <input id="gtinBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gtinBackground)"
                                @input="updateColorField('gtinBackground', $event.target.value)" />
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label class="label">Combined Barcode Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="combinedFormat" class="label">Format</label>
                            <select id="combinedFormat" class="form-control" v-model="combinedFormat">
                                <option value="png">png</option>
                                <option value="jpeg">jpeg</option>
                                <option value="svg">svg</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedBaseurl" class="label">Base URL</label>
                            <input id="combinedBaseurl" class="form-control" type="text" v-model="combinedBaseurl" />
                        </div>
                        <div>
                            <label for="combinedGtinWidth" class="label">GTIN Width</label>
                            <input id="combinedGtinWidth" class="form-control" type="number" min="1" v-model.number="combinedGtinWidth" />
                        </div>
                        <div>
                            <label for="combinedGtinHeight" class="label">GTIN Height</label>
                            <input id="combinedGtinHeight" class="form-control" type="number" min="1" v-model.number="combinedGtinHeight" />
                        </div>
                        <div>
                            <label for="combinedGtinOrientation" class="label">GTIN Orientation</label>
                            <select id="combinedGtinOrientation" class="form-control" v-model="combinedGtinOrientation">
                                <option value="horizontal">horizontal</option>
                                <option value="vertical">vertical</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedGtinFormat" class="label">GTIN Format</label>
                            <select id="combinedGtinFormat" class="form-control" v-model="combinedGtinFormat">
                                <option v-for="format in gtinFormats" :key="`combined-${format}`" :value="format">{{ format }}</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedGtinScale" class="label">GTIN Scale</label>
                            <input id="combinedGtinScale" class="form-control" type="number" min="1" v-model.number="combinedGtinScale" />
                        </div>
                        <div>
                            <label for="combinedIncludeGtinText" class="label">Include GTIN Text</label>
                            <input id="combinedIncludeGtinText" class="form-check-input" type="checkbox" v-model="combinedIncludeGtinText" />
                        </div>
                        <div>
                            <label for="combinedGtinColor" class="label">GTIN Color</label>
                            <input id="combinedGtinColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedGtinColor)"
                                @input="updateColorField('combinedGtinColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedGtinBackground" class="label">GTIN Background</label>
                            <input id="combinedGtinBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedGtinBackground)"
                                @input="updateColorField('combinedGtinBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedQrSizeRatio" class="label">QR Size Ratio</label>
                            <input id="combinedQrSizeRatio" class="form-control" type="number" step="0.01" min="0.1" max="1" v-model.number="combinedQrSizeRatio" />
                        </div>
                        <div>
                            <label for="combinedQrHorizontalRatio" class="label">QR Horizontal Ratio</label>
                            <input id="combinedQrHorizontalRatio" class="form-control" type="number" step="0.01" min="0" max="1" v-model.number="combinedQrHorizontalRatio" />
                        </div>
                        <div>
                            <label for="combinedQrVerticalRatio" class="label">QR Vertical Ratio</label>
                            <input id="combinedQrVerticalRatio" class="form-control" type="number" step="0.01" min="0" max="1" v-model.number="combinedQrVerticalRatio" />
                        </div>
                        <div>
                            <label for="combinedQrColor" class="label">QR Color</label>
                            <input id="combinedQrColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedQrColor)"
                                @input="updateColorField('combinedQrColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedQrBackground" class="label">QR Background</label>
                            <input id="combinedQrBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(combinedQrBackground)"
                                @input="updateColorField('combinedQrBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="combinedQrErrorCorrectionLevel" class="label">QR Error Correction</label>
                            <select id="combinedQrErrorCorrectionLevel" class="form-control" v-model="combinedQrErrorCorrectionLevel">
                                <option value="L">L</option>
                                <option value="M">M</option>
                                <option value="Q">Q</option>
                                <option value="H">H</option>
                            </select>
                        </div>
                        <div>
                            <label for="combinedQrBorder" class="label">QR Border</label>
                            <input id="combinedQrBorder" class="form-control" type="number" min="0" v-model.number="combinedQrBorder" />
                        </div>
                    </div>
                </div>
                <div class="form-group" v-if="gs1Support">
                    <label class="label">GS1 QR Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="gs1QrScale" class="label">Scale</label>
                            <input id="gs1QrScale" class="form-control" type="number" min="1" max="20" v-model.number="gs1QrScale" />
                        </div>
                        <div>
                            <label for="gs1QrIncludeText" class="label">Include Text</label>
                            <input id="gs1QrIncludeText" class="form-check-input" type="checkbox" v-model="gs1QrIncludeText" />
                        </div>
                        <div>
                            <label for="gs1QrTextsize" class="label">Text Size</label>
                            <input id="gs1QrTextsize" class="form-control" type="number" min="1" max="50" v-model.number="gs1QrTextsize" />
                        </div>
                        <div>
                            <label for="gs1QrColor" class="label">Color</label>
                            <input id="gs1QrColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1QrColor)"
                                @input="updateColorField('gs1QrColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gs1QrBackground" class="label">Background</label>
                            <input id="gs1QrBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1QrBackground)"
                                @input="updateColorField('gs1QrBackground', $event.target.value)" />
                        </div>
                    </div>
                </div>
                <div class="form-group" v-if="gs1Support">
                    <label class="label">GS1 DataMatrix Options</label>
                    <div class="optionsGrid">
                        <div>
                            <label for="gs1DataMatrixScale" class="label">Scale</label>
                            <input id="gs1DataMatrixScale" class="form-control" type="number" min="1" max="20" v-model.number="gs1DataMatrixScale" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixIncludeText" class="label">Include Text</label>
                            <input id="gs1DataMatrixIncludeText" class="form-check-input" type="checkbox" v-model="gs1DataMatrixIncludeText" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixTextsize" class="label">Text Size</label>
                            <input id="gs1DataMatrixTextsize" class="form-control" type="number" min="1" max="50" v-model.number="gs1DataMatrixTextsize" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixColor" class="label">Color</label>
                            <input id="gs1DataMatrixColor" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1DataMatrixColor)"
                                @input="updateColorField('gs1DataMatrixColor', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixBackground" class="label">Background</label>
                            <input id="gs1DataMatrixBackground" class="form-control colorPicker" type="color"
                                :value="formatHexColor(gs1DataMatrixBackground)"
                                @input="updateColorField('gs1DataMatrixBackground', $event.target.value)" />
                        </div>
                        <div>
                            <label for="gs1DataMatrixRectangular" class="label">Rectangular</label>
                            <input id="gs1DataMatrixRectangular" class="form-check-input" type="checkbox" v-model="gs1DataMatrixRectangular" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <label for="selectedDpiSelectOption" class="label">Barcode DPI</label>
                <select id="selectedDpiSelectOption" class="form-control" v-model="selectedDpiSelectOption">
                    <option v-for="dpiSelectOption in dpiSelectOptions" :value="dpiSelectOption.value">
                        {{ dpiSelectOption.name }}
                    </option>
                </select>
            </div>
            <button class="col-offset-2 col-3 btn btn-primary" :disabled="!redirectSafetyCanSubmit">Add Code</button>
        </form>
    </div>
</template>

<script>
import { Buffer } from 'buffer'
import { v4 as uuidv4, v6 as uuidv6 } from 'uuid'
import shortUuid from 'short-uuid'
import { component, ref, onUpdated } from 'vue'
import {
    buildShortcodeBaseUri,
    buildGs1DigitalLinkUri,
    buildGs1ElementString,
    createDefaultGs1AiEntries,
    decycle,
    getCodeUrlByShortcode,
    getUser,
    getUserFullNameFromApi,
    normalizeGs1AiEntries,
} from "../../libs/libs.js"
import { apiFetchJson } from "@/libs/api"
import { getUserId } from "@/libs/auth"
import { isValidHttpUrl } from "@/libs/safetyLookup"
import RedirectSafetyStatus from "@/components/RedirectSafetyStatus.vue"
import redirectSafetyValidation from "@/mixins/redirectSafetyValidation"
const uuidTranslator = shortUuid();
const IN_BROWSER = typeof window !== 'undefined'
const USER_AGENT = IN_BROWSER ? window.navigator?.userAgent : undefined
const IS_SAFARI = USER_AGENT?.includes('AppleWebKit') && !USER_AGENT?.includes('Chrome')
//const { v4: uuidv4 } = require('uuid');

function uuidToBase64(uuid) {
    const buffer = Buffer.from(uuid.replace(/-/g, ''), 'hex')
    const str = buffer.toString('base64')
    const strEnc = str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
    return strEnc
}

function calculateUPCCheckDigit(upc) {
    // Ensure the UPC is a string and remove any non-digit characters
    upc = upc.toString().replace(/\D/g, '');

    // Check if the UPC has the correct length (11 digits)
    if (upc.length !== 11) {
        return "Invalid UPC length";
    }

    // Calculate the check digit
    let sumOdd = 0;
    let sumEven = 0;

    for (let i = 0; i < upc.length; i++) {
        const digit = parseInt(upc[i]);

        if ((i + 1) % 2 === 1) {
            sumOdd += digit;
        } else {
            sumEven += digit;
        }
    }

    const total = sumOdd * 3 + sumEven;
    const checkDigit = (10 - (total % 10)) % 10;

    return checkDigit;
}

function isNumeric(str) {
    const num = Number(str);
    return !isNaN(num) && isFinite(num);
}

function sleep(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function validCheckDigit(upc) {
    if (!upc)
        return false;
    if (!isNumeric(upc))
        return false;
    if (typeof upc != 'string')
        return false;
    if (upc.length < 12)
        return false;
    let calcCheckDigit = calculateUPCCheckDigit(upc.substring(0, 11));
    let checkDigit = upc.substring(11, 12)
    if (calcCheckDigit != checkDigit)
        return false;
    return true;
}

export default {
    components: {
        RedirectSafetyStatus,
    },
    mixins: [redirectSafetyValidation],
    data() {
        return {
            owneruserid: '',
            ownerusername: '',
            ownername: '',
            BusName: '',
            codeDescription: '',
            desturl: '',
            phone: '',
            location: '',
            upc: '',
            upcSuggestion: '12345678901',
            mappedUpc: '',
            codeid: uuidv6(),
            shortcodeid: null,
            sourceurl: '',
            redirecturl: '',
            showAdvancedBarcodeOptions: false,
            calcCheckDigit: false,
            upcError: '',
            upcBarcode: '',
            qrCodeImageData: null,
            upcImageData: null,
            overlaidData: null,
            overlaidImageData: null,
            dpiSelectOptions: [
                {
                    name: "Web (96dpi)",
                    value: 96
                },
                {
                    name: "Print (384dpi)",
                    value: 384
                },
            ],
            selectedDpiSelectOption: 96,
            selectedStyleSelectOption: 1,
            BusNameNote: '',
            messageNote: '',
            upcSupport: false,
            gs1Support: false,
            displayUserStuffDetail: false,
            imageFile: null,
            gs1DigitalLinkBaseUri: "",
            gs1AiEntries: createDefaultGs1AiEntries(),
            gtinFormats: ["default", "ean13", "ean8", "upca", "upce", "code39", "code128", "itf14"],
            qrWidth: 600,
            qrColor: "000000",
            qrBackground: "FFFFFF",
            qrErrorCorrectionLevel: "M",
            qrBorder: 1,
            qrBaseurl: "",
            gtinFormat: "default",
            gtinWidth: 400,
            gtinHeight: 60,
            gtinScale: 3,
            gtinIncludeText: true,
            gtinTextsize: 12,
            gtinColor: "000000",
            gtinBackground: "FFFFFF",
            combinedFormat: "png",
            combinedBaseurl: "",
            combinedGtinWidth: 400,
            combinedGtinHeight: 60,
            combinedGtinOrientation: "horizontal",
            combinedGtinFormat: "default",
            combinedGtinScale: 3,
            combinedIncludeGtinText: true,
            combinedGtinColor: "000000",
            combinedGtinBackground: "FFFFFF",
            combinedQrSizeRatio: 0.6,
            combinedQrHorizontalRatio: 0.5,
            combinedQrVerticalRatio: 0.6,
            combinedQrColor: "000000",
            combinedQrBackground: "FFFFFF",
            combinedQrErrorCorrectionLevel: "M",
            combinedQrBorder: 1,
            gs1QrScale: 4,
            gs1QrIncludeText: false,
            gs1QrTextsize: 10,
            gs1QrColor: "000000",
            gs1QrBackground: "FFFFFF",
            gs1DataMatrixScale: 4,
            gs1DataMatrixIncludeText: false,
            gs1DataMatrixTextsize: 10,
            gs1DataMatrixColor: "000000",
            gs1DataMatrixBackground: "FFFFFF",
            gs1DataMatrixRectangular: false,
            gs1DigitalLinkBaseUriHelpText: "Leaving this blank is recommended. QR Code Experts will generate the right URL automatically so this code gets the platform's full feature set.",
        }
    },
    computed: {
        derivedGs1AiEntries() {
            return this.resolveGs1AiEntries();
        },
        derivedGs1ElementString() {
            return buildGs1ElementString(this.derivedGs1AiEntries);
        },
        derivedGs1DigitalLinkUri() {
            const rawGs1DigitalLinkBaseUri = `${this.gs1DigitalLinkBaseUri ?? ""}`.trim();
            if (!rawGs1DigitalLinkBaseUri) return "";
            return buildGs1DigitalLinkUri(this.derivedGs1AiEntries, rawGs1DigitalLinkBaseUri);
        },
    },
    watch: {
        gs1Support(enabled) {
            if (enabled) {
                this.syncGs1Ai01FromGtin();
            }
        },
        upc() {
            this.syncGs1Ai01FromGtin();
        },
    },
    methods: {
        handleFileChange(event) {
            // this.imageFile = event.target.files[0];
        },
        jsonPretty(val, indent = 2) {
            if (typeof val === "string") {
                val = JSON.parse(val)
            }
            return JSON.stringify(val, null, indent)
        },
        toggleAdvancedBarcodeOptions() {
            this.showAdvancedBarcodeOptions = !this.showAdvancedBarcodeOptions
        },
        formatHexColor(value) {
            const raw = `${value ?? ""}`.trim().replace(/^#/, "");
            if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
                return "#000000";
            }
            return `#${raw.toUpperCase()}`;
        },
        updateColorField(fieldName, value) {
            this[fieldName] = `${value ?? ""}`.trim().replace(/^#/, "").toUpperCase();
        },
        addGs1AiEntry() {
            this.gs1AiEntries.push({ ai: "", value: "", isDefault: false });
        },
        removeGs1AiEntry(index) {
            const entry = this.gs1AiEntries[index];
            if (!entry || entry.isDefault) return;
            this.gs1AiEntries.splice(index, 1);
        },
        syncGs1Ai01FromGtin() {
            if (!(this.gs1Support && this.upcSupport && validCheckDigit(this.upc))) return;
            const gtinEntry = this.gs1AiEntries.find((entry) => `${entry?.ai ?? ""}`.trim() === "01");
            if (!gtinEntry) return;
            gtinEntry.value = `${this.upc}`.padStart(14, "0");
        },
        resolveGs1AiEntries() {
            const entries = normalizeGs1AiEntries(this.gs1AiEntries).map((entry) => ({ ...entry }));
            const gtinEntry = entries.find((entry) => entry.ai === "01");
            if (gtinEntry && gtinEntry.value.length === 0 && this.gs1Support && this.upcSupport && validCheckDigit(this.upc)) {
                gtinEntry.value = `${this.upc}`.padStart(14, "0");
            }
            return entries;
        },
        handleDisplayUserStuffDetail() {
            this.displayUserStuffDetail = !this.displayUserStuffDetail
        },
        checkUPC(evt) {
            let res
            //console.log(evt)
            if (!isNumeric(this.upc)) {
                this.upcError = "UPC must be a number"
                this.mappedUpc = ''
            }
            if (isNumeric(this.upc) && this.upc.length < 11) {
                this.upcError = "UPC must be at least 11 digits long"
                this.mappedUpc = ''
            }
            if (isNumeric(this.upc) && this.upc.length == 11) {
                res = calculateUPCCheckDigit(this.upc)
                this.upcError = `UPC with check digit - ${res} - ${this.upc}${res}`
                this.mappedUpc = ''
            }
            if (isNumeric(this.upc) && this.upc.length == 11 && this.calcCheckDigit) {
                res = calculateUPCCheckDigit(this.upc)
                this.upc = this.upc + res
                this.upcError = ''
                this.mappedUpc = this.upc
            }
            if (this.upc.length == 12) {
                let check = this.upc[11];
                let correctCheck = calculateUPCCheckDigit(this.upc.substring(0, 11));
                if (check != correctCheck) {
                    this.upcError = `Check digit invalid. Barcode will not generate. Check digit should be ${correctCheck}`
                } else {
                    this.upcError = ''
                    this.mappedUpc = this.upc
                }
            }
            if (this.upc.length > 12) {
                this.upcError = `UPC value has too many digits ${this.upc.length}`
            }

        },
        handleSubmit() {
            if (!this.redirectSafetyCanSubmit) {
                this.messageNote = 'Redirect URL must pass safety lookup before saving.';
                return;
            }
            const gs1Enabled = this.upcSupport && this.gs1Support;
            if (this.desturl.length == 0 && this.phone.length == 0 && this.location.length == 0 && this.redirecturl.length == 0) {
                this.messageNote = 'One of these must be populated'
                return
            } else {
                this.messageNote = ''
            }
            const rawGs1DigitalLinkBaseUri = `${this.gs1DigitalLinkBaseUri ?? ""}`.trim();
            if (gs1Enabled && rawGs1DigitalLinkBaseUri && !isValidHttpUrl(rawGs1DigitalLinkBaseUri)) {
                this.messageNote = "GS1 Digital Link base URI must be valid.";
                return;
            }
            const gs1AiEntries = this.resolveGs1AiEntries();
            const populatedGs1AiEntries = gs1AiEntries.filter((entry) => entry.value.length > 0);
            const hasGtInAi = populatedGs1AiEntries.some((entry) => entry.ai === "01");
            const duplicateAis = populatedGs1AiEntries
                .map((entry) => entry.ai)
                .filter((ai, index, list) => list.indexOf(ai) !== index);
            if (gs1Enabled && duplicateAis.length > 0) {
                this.messageNote = `GS1 AIs must be unique. Duplicate AI: ${duplicateAis[0]}`;
                return;
            }
            if (gs1Enabled && populatedGs1AiEntries.some((entry) => !/^\d{2,4}$/.test(entry.ai))) {
                this.messageNote = "GS1 AIs must be 2 to 4 digits.";
                return;
            }
            const ai01Entry = populatedGs1AiEntries.find((entry) => entry.ai === "01");
            if (gs1Enabled && ai01Entry && !/^\d{14}$/.test(ai01Entry.value)) {
                this.messageNote = "AI 01 must be a 14-digit GTIN.";
                return;
            }
            const ai17Entry = populatedGs1AiEntries.find((entry) => entry.ai === "17");
            if (gs1Enabled && ai17Entry && !/^\d{6}$/.test(ai17Entry.value)) {
                this.messageNote = "AI 17 must use YYMMDD format.";
                return;
            }
            if (gs1Enabled && populatedGs1AiEntries.length > 0 && !hasGtInAi) {
                this.messageNote = "AI 01 (GTIN) is required for GS1 Digital Link output.";
                return;
            }
            let code = {
                userid: this.owneruserid,
                title: this.BusName,
                description: this.codeDescription || this.BusName,
                redirectUrl: null,
                phone: null,
                address: null,
                gtin: null,
                autoFixGtinCheckDigit: false,
                metadata: {
                    selectedDpiSelectOption: this.selectedDpiSelectOption,
                    selectedStyleSelectOption: this.selectedStyleSelectOption,
                    sourceurl: this.sourceurl,
                    upcSupport: this.upcSupport,
                    gs1Support: this.gs1Support,
                },
            }

            //validation before save - form should have it's own validation.
            if (isValidHttpUrl(this.desturl))
                code.redirectUrl = this.desturl
            if (this.phone && this.phone != '')
                code.phone = this.phone
            if (this.location && this.location != '')
                code.address = this.location
            if (isValidHttpUrl(this.redirecturl))
                code.redirectUrl = this.redirecturl
            if (this.upcSupport) {
                if (validCheckDigit(this.upc)) {
                    code.autoFixGtinCheckDigit = this.calcCheckDigit
                    code.metadata.upcSupport = this.upcSupport
                    code.gtin = this.upc
                } else {
                    console.log(`UPC invalid. Not saved ${this.upc} check digit invalid`)
                }
            }
            if (gs1Enabled && populatedGs1AiEntries.length > 0) {
                const derivedGs1DigitalLinkUri = rawGs1DigitalLinkBaseUri
                    ? buildGs1DigitalLinkUri(populatedGs1AiEntries, rawGs1DigitalLinkBaseUri)
                    : "";
                code.gs1 = {
                    aiEntries: populatedGs1AiEntries.map(({ ai, value }) => ({ ai, value })),
                    digitalLinkBaseUri: rawGs1DigitalLinkBaseUri,
                    digitalLinkUri: derivedGs1DigitalLinkUri,
                    elementString: buildGs1ElementString(populatedGs1AiEntries) || undefined,
                };
            }
            code.qr = {
                width: this.qrWidth,
                color: this.qrColor,
                background: this.qrBackground,
                errorCorrectionLevel: this.qrErrorCorrectionLevel,
                border: this.qrBorder,
                baseurl: this.qrBaseurl,
            };
            code.gtinOptions = {
                format: this.gtinFormat,
                width: this.gtinWidth,
                height: this.gtinHeight,
                scale: this.gtinScale,
                includetext: this.gtinIncludeText,
                textsize: this.gtinTextsize,
                color: this.gtinColor,
                background: this.gtinBackground,
            };
            code.combinedBarcode = {
                gtinWidth: this.combinedGtinWidth,
                gtinHeight: this.combinedGtinHeight,
                gtinOrientation: this.combinedGtinOrientation,
                gtinFormat: this.combinedGtinFormat,
                gtinScale: this.combinedGtinScale,
                includeGtinText: this.combinedIncludeGtinText,
                gtinColor: this.combinedGtinColor,
                gtinBackground: this.combinedGtinBackground,
                qrSizeRatio: this.combinedQrSizeRatio,
                qrHorizontalRatio: this.combinedQrHorizontalRatio,
                qrVerticalRatio: this.combinedQrVerticalRatio,
                qrColor: this.combinedQrColor,
                qrBackground: this.combinedQrBackground,
                qrErrorCorrectionLevel: this.combinedQrErrorCorrectionLevel,
                qrBorder: this.combinedQrBorder,
                format: this.combinedFormat,
                baseurl: this.combinedBaseurl,
            };
            if (gs1Enabled && populatedGs1AiEntries.length > 0) {
                code.gs1Qr = {
                    scale: this.gs1QrScale,
                    includetext: this.gs1QrIncludeText,
                    textsize: this.gs1QrTextsize,
                    color: this.gs1QrColor,
                    background: this.gs1QrBackground,
                };
            }
            if (gs1Enabled && populatedGs1AiEntries.length > 0) {
                code.gs1DataMatrix = {
                    scale: this.gs1DataMatrixScale,
                    includetext: this.gs1DataMatrixIncludeText,
                    textsize: this.gs1DataMatrixTextsize,
                    color: this.gs1DataMatrixColor,
                    background: this.gs1DataMatrixBackground,
                    rectangular: this.gs1DataMatrixRectangular,
                };
            }
            apiFetchJson("codes", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(code),
            })
                .then((newcode) => {
                    console.log(`handleSubmit response ${JSON.stringify(newcode)}`)
                    const shortcode = newcode.shortcode || newcode.shortcodeid
                    if (shortcode) {
                        const sourceurl = `${process.env.VUE_APP_ROOT_URL}codead/${shortcode}?of=1`
                        const shortcodeBaseUri =
                            code.gs1 && !(`${code.gs1.digitalLinkBaseUri ?? ""}`.trim())
                                ? buildShortcodeBaseUri(shortcode)
                                : code.gs1?.digitalLinkBaseUri
                        const updatePayload = {
                            ...code,
                            gs1: code.gs1 ? {
                                ...code.gs1,
                                digitalLinkBaseUri: shortcodeBaseUri ?? code.gs1.digitalLinkBaseUri,
                                digitalLinkUri: shortcodeBaseUri
                                    ? buildGs1DigitalLinkUri(code.gs1.aiEntries || [], shortcodeBaseUri) || undefined
                                    : code.gs1.digitalLinkUri,
                            } : code.gs1,
                            metadata: {
                                ...code.metadata,
                                sourceurl,
                            },
                        }
                        apiFetchJson(getCodeUrlByShortcode(shortcode), {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(updatePayload),
                        }, { auth: true }).catch((err) => {
                            console.log(`sourceurl update failed ${JSON.stringify(err)}`)
                        })
                        this.$router.push(`/code/${shortcode}`)
                    } else {
                        this.$router.push(`/codes`)
                    }
                })
                .catch((err) => {
                    this.messageNote = err?.message || "Add Code failed.";
                    console.log(`handleSubmit fetch failed ${JSON.stringify(err)}`)
                })
            /*
            //Upload Image
            const formData = new FormData()
            formData.append("file", selectedFile.value)
            imageuri = `${process.env.VUE_APP_API_BASE_URL}imageapi`
            fetch(imageuri, {
                method: 'POST',
                body: formData,
            })
                .then((response) => response.ok)
                .then((isOK) => {
                    if (isOK) {
                        
                    } else {
                        console.log('Upload of image failed.');
                    }
                })
                    */
        },

    },
    mounted() {
        this.shortcodeid = uuidToBase64(this.codeid)////uuidTranslator.fromUUID(this.codeid)
        this.sourceurl = ''
        let setupUpc = ref('#upc')
        this.owneruserid = getUserId()
        if (!this.owneruserid) {
            this.$router.push({ path: "/auth" })
            return
        }
        getUser(this.owneruserid)
            .then((data) => {
                this.user = data
                this.ownerusername = this.user.username
                this.ownername = getUserFullNameFromApi(this.user)
            })
            .catch((err) => console.log(`mounted load user err ${JSON.stringify(err)}`))

        if (setupUpc.children && setupUpc.children[0]) {
            setupUpc.children[0].onChange((evt) => {
                let event = evt
                console.log(`child changed. ${event}`)
            })
        }
    },
    onChange() {
        console.log(`This is a test.... onChange`)
    },
    setup() {
    }

}
</script>

<style>
.fatfinger {
    margin-top: 25px;
    margin-bottom: 25px;
}

input.form-control,
select.form-control,
textarea.form-control {
    background: var(--qrce-surface-2);
    border: 1px solid rgba(15, 23, 42, 0.22);
    border-radius: 12px;
    color: var(--qrce-text);
    padding: 10px 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

input.form-control:hover,
select.form-control:hover,
textarea.form-control:hover {
    border-color: rgba(15, 23, 42, 0.32);
}

input.form-control::placeholder,
textarea.form-control::placeholder {
    color: rgba(15, 23, 42, 0.55);
}

.xForm {
    max-width: 586px;
    margin: 20px auto;
    background: var(--qrce-surface);
    text-align: left;
    padding: 20px;
    border-radius: var(--qrce-radius);
    border: 1px solid var(--qrce-border);
    border-left: 4px solid var(--qrce-primary);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
}

@media (max-width: 819px) {
    .xForm {
        width: calc(100% - 24px);
        margin: 14px 12px;
    }
}

.formSectionLabel {
    font-size: 0.9em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}

.form-control {
    text-align: left;
    margin-top: 5px;
    display: block;
    width: 100%;
}

.form-control label {
    order: 1;
    color: #232323;
    /*display: inline-block;*/
    margin: 5px 0 1px;
    font-size: 0.8em;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
}

.form-control input {
    order: 2;
    /*display: block;*/
    margin: 5px 0 5px;
    padding: 10px 6px;
    width: 100%;
    box-sizing: border-box;
    border: 0;
    border-bottom: 1px solid #ddd;
    color: #555;
}

.form-control select {
    order: 2;
    /*display: block;*/
    margin: 5px 0 5px;
    padding: 10px 6px;
    width: 100%;
    box-sizing: border-box;
    border: 0;
    border-bottom: 1px solid #ddd;
    color: #555;
}

.form-control input[type="checkbox"] {
    order: 1;
    display: inline-block;
    width: 16px;
    margin: 5px 10px 5px 5px;
    position: relative;
    top: 2px;
}

div.notes {
    order: 99;
    margin-left: 10px;
}

div.upcError {
    order: 99;
    color: red;
    margin-left: 10px;
}

div.noteError {
    color: red;
    margin-left: 10px;
    margin-bottom: 20px;
}

.btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

input[required]+label:after {
    content: '*';
    color: red;
}

.thisOr {
    border: 3px solid blue;
    padding: 10px;
    margin: 10px;
}

textarea {
    border: 1px solid #ddd;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    height: 100px;
}

.optionsGrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-top: 10px;
}

.labelWithHelp {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.helpLink {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    border: 1px solid var(--qrce-border);
    color: var(--qrce-primary);
    text-decoration: none;
    font-weight: 700;
    background: var(--qrce-surface);
}

.helpLink:hover {
    text-decoration: none;
    border-color: var(--qrce-primary);
}

.gs1AiRow {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
}

.gs1AiCode {
    text-align: center;
}

.gs1AiValue {
    min-width: 0;
}

.colorPicker {
    min-height: 44px;
    padding: 6px;
}

.btn-primary {
    display: block;
    margin: 20px auto 0;
    background: rgb(46, 108, 183);
    color: white;
    padding: 10px;
    border: 0;
    border-radius: 6px;
    font-size: 16px;
}

#qrcode {
    width: 50px;
    height: 50px;
}

.qrCodeImage {
    display: inline-block;
    border: 2px red;
    padding: 5px;
    margin: 5px;
    width: 100px;
    height: 100px;
    /*display: none;*/
}

.upcImage {
    display: inline-block;
    border: 2px green;
    padding: 5px;
    margin: 5px;
    /*width: 242px;
    height: 142px;*/
    /*display: none;*/
}

.overlaidImage {
    display: inline-block;
    width: 484px;
    height: 284px;
    border: 2px blue;
    padding: 5px;
    margin: 5px;
}

.overlaidImageCanvas {
    display: inline-block;
    width: 484px;
    height: 284px;
    border: 2px rgb(255, 0, 230);
    padding: 5px;
    margin: 5px;
}
</style>
